import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import {
  ArrowLeft,
  Save,
  Layers,
  Info
} from "lucide-react";
import { getMe, getAdminModules, saveAdminModule } from "../../api/client";
import { useTranslation } from "../../hooks/useTranslation";

export default function ModuleCreator() {
  const { t } = useTranslation();
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const isNew = moduleId === "new";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  const [form, setForm] = useState({
    id: "",
    title: "",
    year_level: "1",
    semester: "1",
    order_index: 1,
    description: ""
  });

  const [validation, setValidation] = useState({});

  // Calculate semester options based on year level (continuous: year 1 = sem 1-2, year 2 = sem 3-4, etc.)
  const getSemesterOptions = (year) => {
    const yearNum = parseInt(year);
    const firstSem = (yearNum - 1) * 2 + 1;
    const secondSem = firstSem + 1;
    return [
      { value: String(firstSem), label: `${firstSem}. Semester` },
      { value: String(secondSem), label: `${secondSem}. Semester` }
    ];
  };

  const handleYearChange = (newYear) => {
    // Reset semester to first option of new year
    const firstSem = (parseInt(newYear) - 1) * 2 + 1;
    setForm(prev => ({
      ...prev,
      year_level: newYear,
      semester: String(firstSem)
    }));
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, [moduleId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // TESTING MODE: Skip auth check
      const me = await getMe().catch(() => null);
      // Allow access without login for testing

      if (!isNew) {
        // Load existing module
        const modules = await getAdminModules();
        const existingModule = modules.find(
          (m) => m.id.toUpperCase() === moduleId.toUpperCase()
        );

        if (existingModule) {
          setForm({
            id: existingModule.id,
            title: existingModule.title || "",
            year_level: existingModule.year_level || "1",
            semester: existingModule.semester || "1",
            order_index: existingModule.order_index || 1,
            description: existingModule.description || ""
          });
        } else {
          setError(t.contentCreator?.moduleNotFound || "Modul nicht gefunden");
        }
      }
    } catch (err) {
      console.error("ModuleCreator load error:", err);
      setError(t.contentCreator?.loadError || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.id.trim()) {
      errors.id = t.contentCreator?.idRequired || "ID ist erforderlich";
    } else if (!/^[A-Za-z0-9_]+$/.test(form.id)) {
      errors.id = t.contentCreator?.idInvalid || "ID darf nur Buchstaben, Zahlen und Unterstriche enthalten";
    }

    if (!form.title.trim()) {
      errors.title = t.contentCreator?.titleRequired || "Titel ist erforderlich";
    }

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await saveAdminModule(
        {
          id: form.id.toUpperCase(),
          title: form.title,
          year_level: form.year_level,
          semester: form.semester,
          order_index: parseInt(form.order_index) || 1,
          description: form.description
        },
        isNew
      );

      setSuccess(
        isNew
          ? t.contentCreator?.moduleCreated || "Modul erfolgreich erstellt"
          : t.contentCreator?.moduleSaved || "Modul erfolgreich gespeichert"
      );

      // Redirect after short delay
      setTimeout(() => {
        navigate("/teacher/content");
      }, 1500);
    } catch (err) {
      console.error("Save error:", err);
      setError(t.contentCreator?.saveError || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validation[field]) {
      setValidation((prev) => ({ ...prev, [field]: null }));
    }
  };

  const generateId = () => {
    const prefix = "M";
    const random = Math.floor(Math.random() * 100);
    const suffix = "_WR_1K"; // Default suffix
    const newId = `${prefix}${random}${suffix}`;
    handleChange("id", newId);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">{t.common?.loading || "Lädt..."}</p>
      </Container>
    );
  }

  return (
    <main
      className="module-creator-main"
      style={{
        backgroundColor: "var(--bg-secondary)",
        minHeight: "100vh"
      }}
    >
      <Container
        className="py-4"
        style={{ maxWidth: "800px" }}
      >
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
          <Button
            variant="link"
            className="p-0 me-3 text-secondary"
            onClick={() => navigate("/teacher/content")}
          >
            <ArrowLeft size={24} />
          </Button>
          <div>
            <h1 className="mb-0 h3">
              <Layers size={24} className="me-2 text-primary" />
              {isNew
                ? t.contentCreator?.createModule || "Modul erstellen"
                : t.contentCreator?.editModule || "Modul bearbeiten"}
            </h1>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success">
            {success}
          </Alert>
        )}

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              {/* Module ID */}
              <Form.Group className="mb-3">
                <Form.Label>{t.contentCreator?.moduleId || "Modul-ID"} *</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={form.id}
                    onChange={(e) => handleChange("id", e.target.value.toUpperCase())}
                    placeholder="z.B. M1_WR_1K"
                    isInvalid={!!validation.id}
                    disabled={!isNew}
                  />
                  {isNew && (
                    <Button variant="outline-secondary" onClick={generateId} type="button">
                      {t.contentCreator?.generate || "Generieren"}
                    </Button>
                  )}
                </div>
                <Form.Control.Feedback type="invalid">
                  {validation.id}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  <Info size={12} className="me-1" />
                  {t.contentCreator?.idHint || "Eindeutige ID, z.B. M1_WR_1K für Modul 1 Wirtschaftliches Rechnen Klasse 1"}
                </Form.Text>
              </Form.Group>

              {/* Title */}
              <Form.Group className="mb-3">
                <Form.Label>{t.contentCreator?.moduleTitle || "Titel"} *</Form.Label>
                <Form.Control
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="z.B. Modul 1: Wirtschaftliches Rechnen"
                  isInvalid={!!validation.title}
                />
                <Form.Control.Feedback type="invalid">
                  {validation.title}
                </Form.Control.Feedback>
              </Form.Group>

              {/* Year Level & Semester */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>{t.contentCreator?.yearLevel || "Jahrgang/Klasse"}</Form.Label>
                    <Form.Select
                      value={form.year_level}
                      onChange={(e) => handleYearChange(e.target.value)}
                    >
                      <option value="1">1. Klasse</option>
                      <option value="2">2. Klasse</option>
                      <option value="3">3. Klasse</option>
                      <option value="4">4. Klasse</option>
                      <option value="5">5. Klasse</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>{t.contentCreator?.semester || "Semester"}</Form.Label>
                    <Form.Select
                      value={form.semester}
                      onChange={(e) => handleChange("semester", e.target.value)}
                    >
                      {getSemesterOptions(form.year_level).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Order Index */}
              <Form.Group className="mb-3">
                <Form.Label>{t.contentCreator?.orderIndex || "Reihenfolge"}</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={form.order_index}
                  onChange={(e) => handleChange("order_index", e.target.value)}
                />
                <Form.Text className="text-muted">
                  {t.contentCreator?.orderHint || "Bestimmt die Anzeigereihenfolge der Module"}
                </Form.Text>
              </Form.Group>

              {/* Description */}
              <Form.Group className="mb-4">
                <Form.Label>{t.contentCreator?.description || "Beschreibung"}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder={t.contentCreator?.descriptionPlaceholder || "Kurze Beschreibung des Moduls..."}
                />
              </Form.Group>

              {/* Buttons */}
              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate("/teacher/content")}
                  disabled={saving}
                >
                  <ArrowLeft size={16} className="me-1" />
                  {t.common?.back || "Zurück"}
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {t.common?.loading || "Lädt..."}
                    </>
                  ) : (
                    <>
                      <Save size={16} className="me-1" />
                      {t.common?.save || "Speichern"}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}
